import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, Inbox, X, Filter, Calendar, Paperclip, CheckCircle2, ChevronDown, Archive, KanbanSquare } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import api, { formatApiErrorDetail } from "../lib/api";
import { Calendar as CalendarPicker } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { PRIORITIES, dueState, Avatar, statusRule } from "../lib/helpers";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { useAuth } from "../context/AuthContext";
import TaskModal from "../components/TaskModal";
import { celebrate } from "../lib/confetti";
import { PageSkeleton } from "../components/Skeleton";

const DUE_CLS = { overdue: "text-red-400", soon: "text-amber-400" };
const inputCls = "bg-[#0A0A0B] border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-primary";

const downloadAtt = async (att) => {
  try {
    const r = await api.get(`/attachments/${att.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = att.original_filename; a.click();
    URL.revokeObjectURL(url);
  } catch { toast.error("No se pudo descargar"); }
};

const GROUPS = [
  { id: "past", label: "Anteriores", color: "#FF1744" },
  { id: "week", label: "Esta semana", color: "#FFD600" },
  { id: "month", label: "Este mes", color: "#00B0FF" },
  { id: "later", label: "Más adelante", color: "#D500F9" },
  { id: "none", label: "Sin fecha", color: "#71717A" },
];

const isoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function bucketOf(t) {
  if (!t.due_date) return "none";
  const due = t.due_date.slice(0, 10);
  const now = new Date();
  const today = isoDate(now);
  const dow = (now.getDay() + 6) % 7;
  const eow = new Date(now); eow.setDate(now.getDate() + (6 - dow));
  const eom = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  if (due < today) return "past";
  if (due <= isoDate(eow)) return "week";
  if (due <= isoDate(eom)) return "month";
  return "later";
}

function Row({ t, users, depName, onOpen, onStatus, groupColor, delay = 0 }) {
  const pr = PRIORITIES[t.priority] || PRIORITIES.medium;
  const ds = dueState(t.due_date, t.is_done);
  const col = (t.board?.columns || []).find((c) => c.id === t.status);
  const rowColor = t.color || col?.color || "#71717A";
  const assignees = (t.assignee_ids || []).map((id) => users.find((u) => u.id === id)).filter(Boolean);
  return (
    <tr onClick={() => onOpen(t)} data-testid={`mytask-row-${t.id}`}
      style={{ background: `${rowColor}0d`, boxShadow: `inset 3px 0 0 ${groupColor}`, animationDelay: `${delay}ms` }}
      className="border-b border-white/5 hover:bg-white/[0.05] cursor-pointer transition-colors animate-fade-up">
      <td className="px-4 py-3 min-w-[180px]">
        <p className={`text-sm font-medium flex items-center gap-2 ${t.is_done ? "line-through text-zinc-500" : ""}`}>
          <span className="h-2 w-2 rounded-full shrink-0" style={{ background: rowColor }} data-testid={`task-color-dot-${t.id}`} />
          {t.title}
        </p>
        <p className="text-xs text-zinc-600 ml-4">{t.board?.name}</p>
      </td>
      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
        <div className="inline-flex items-center gap-2 bg-[#0A0A0B] border border-white/10 rounded-lg pl-2.5 pr-1 py-1.5 hover:border-white/25 transition-colors">
          <span className="h-2 w-2 rounded-full shrink-0" style={{ background: col?.color || "#71717A" }} />
          <select data-testid={`status-select-${t.id}`} value={t.status} onChange={(e) => onStatus(t, e.target.value)}
            className="bg-transparent text-xs font-medium outline-none cursor-pointer max-w-[120px]">
            {(t.board?.columns || []).map((c) => <option key={c.id} value={c.id} className="bg-[#121214]">{c.title}</option>)}
          </select>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className="text-[11px] font-bold px-2.5 py-1 rounded-full" style={{ background: `${pr.color}1e`, color: pr.color }}>{pr.label}</span>
      </td>
      <td className="px-4 py-3 text-sm text-zinc-400 whitespace-nowrap" data-testid={`dept-cell-${t.id}`}>
        {depName[t.department_id] || "—"}
      </td>
      <td className="px-4 py-3 min-w-[110px]" data-testid={`progress-cell-${t.id}`}>
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden min-w-[50px]">
            <div className="h-full rounded-full" style={{ width: `${t.is_done ? 100 : t.progress || 0}%`, background: t.is_done ? "#00E676" : "#FFD600" }} />
          </div>
          <span className={`text-[11px] font-bold w-8 text-right ${t.is_done ? "text-[#00E676]" : "text-[#FFD600]"}`}>{t.is_done ? 100 : t.progress || 0}%</span>
        </div>
      </td>
      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()} data-testid={`attachments-cell-${t.id}`}>
        {(t.attachments || []).length === 0 ? <span className="text-xs text-zinc-600">—</span> : (
          <div className="space-y-1">
            {t.attachments.slice(0, 2).map((a) => (
              <button key={a.id} onClick={() => downloadAtt(a)} data-testid={`att-link-${a.id}`} title={`Descargar ${a.original_filename}`}
                className="flex items-center gap-1.5 text-xs text-[#00B0FF] hover:underline max-w-[160px]">
                <Paperclip className="h-3 w-3 shrink-0" /><span className="truncate">{a.original_filename}</span>
              </button>
            ))}
            {t.attachments.length > 2 && <span className="text-[11px] text-zinc-500">+{t.attachments.length - 2} más</span>}
          </div>
        )}
      </td>
      <td className="px-4 py-3">
        {assignees.length === 0 ? <span className="text-xs text-zinc-600">Sin asignar</span> : (
          <div className="flex -space-x-2">
            {assignees.slice(0, 3).map((u) => <Avatar key={u.id} user={u} size={24} className="ring-2 ring-[#121214]" />)}
          </div>
        )}
      </td>
      <td className={`px-4 py-3 text-xs whitespace-nowrap ${DUE_CLS[ds] || "text-zinc-500"}`}>
        {t.is_done ? (
          <span className="inline-flex items-center gap-1 text-[#00E676]" data-testid={`completed-date-${t.id}`}
            title={t.due_date ? `Vencía: ${new Date(t.due_date).toLocaleDateString("es", { day: "numeric", month: "short", year: "numeric" })}` : ""}>
            <CheckCircle2 className="h-3 w-3" />
            {(t.status_changed_at || t.created_at)
              ? `Completada ${new Date(t.status_changed_at || t.created_at).toLocaleDateString("es", { day: "numeric", month: "short", year: "numeric" })}`
              : "Completada"}
          </span>
        ) : (
          t.due_date ? new Date(t.due_date).toLocaleDateString("es", { day: "numeric", month: "short", year: "numeric" }) : "Sin fecha"
        )}
      </td>
    </tr>
  );
}

function CreateTaskDialog({ boards, departments, meId, onClose, onCreated }) {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [boardId, setBoardId] = useState(boards[0]?.id || "");
  const [status, setStatus] = useState(boards[0]?.columns?.[0]?.id || "");
  const [priority, setPriority] = useState("medium");
  const [depId, setDepId] = useState("");
  const [due, setDue] = useState(null);
  const [dueOpen, setDueOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const board = boards.find((b) => b.id === boardId);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !boardId || !status) return;
    setSaving(true);
    try {
      await api.post("/tasks", {
        board_id: boardId, title: title.trim(), status, priority,
        due_date: due ? format(due, "yyyy-MM-dd") : null, department_id: depId || null, assignee_ids: [meId],
      });
      toast.success("Tarea creada");
      onCreated();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo crear la tarea");
    } finally { setSaving(false); }
  };

  if (boards.length === 0) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
        <div className="w-full max-w-md bg-[#121214] border border-white/10 rounded-2xl p-6 animate-pop text-center" onClick={(e) => e.stopPropagation()} data-testid="create-task-no-boards">
          <KanbanSquare className="h-10 w-10 text-[#00B0FF] mx-auto mb-3 animate-float" />
          <h2 className="font-display text-lg font-bold">Primero necesitas un tablero</h2>
          <p className="text-sm text-zinc-500 mt-1 mb-5">Las tareas viven dentro de un tablero. Crea uno y vuelve aquí para añadir tu primera tarea.</p>
          <button onClick={() => { onClose(); navigate("/boards"); }} data-testid="go-create-board"
            className="w-full bg-primary hover:bg-[#FF5047] text-white font-semibold rounded-lg py-2.5 text-sm">
            Ir a crear un tablero
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <form onSubmit={submit} className="w-full max-w-md bg-[#121214] border border-white/10 rounded-2xl p-6 animate-fade-up space-y-4" onClick={(e) => e.stopPropagation()} data-testid="create-task-dialog">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-bold">Nueva tarea</h2>
          <button type="button" onClick={onClose}><X className="h-5 w-5 text-zinc-500 hover:text-white" /></button>
        </div>
        <div>
          <label className="text-xs text-zinc-500">Título</label>
          <input autoFocus required value={title} onChange={(e) => setTitle(e.target.value)} data-testid="create-task-title" className={`${inputCls} w-full mt-1`} placeholder="¿Qué hay que hacer?" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-zinc-500">Tablero</label>
            <select value={boardId} data-testid="create-task-board"
              onChange={(e) => { setBoardId(e.target.value); setStatus(boards.find((b) => b.id === e.target.value)?.columns?.[0]?.id || ""); }}
              className={`${inputCls} w-full mt-1`}>
              {boards.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-zinc-500">Estado</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="create-task-status" className={`${inputCls} w-full mt-1`}>
              {(board?.columns || []).map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-zinc-500">Prioridad</label>
            <select value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="create-task-priority" className={`${inputCls} w-full mt-1`}>
              {Object.entries(PRIORITIES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-zinc-500">Departamento</label>
            <select value={depId} onChange={(e) => setDepId(e.target.value)} data-testid="create-task-department" className={`${inputCls} w-full mt-1`}>
              <option value="">—</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-xs text-zinc-500">Vencimiento</label>
            <Popover open={dueOpen} onOpenChange={setDueOpen}>
              <PopoverTrigger asChild>
                <button type="button" data-testid="create-task-due"
                  className={`${inputCls} w-full mt-1 flex items-center gap-2 text-left ${due ? "" : "text-zinc-500"}`}>
                  <Calendar className="h-4 w-4 text-zinc-500 shrink-0" />
                  <span className="truncate capitalize">
                    {due ? format(due, "EEEE, d 'de' MMMM 'de' yyyy", { locale: es }) : "Elegir fecha (opcional)"}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 z-[80] bg-[#121214] border-white/10 text-white" align="start">
                <CalendarPicker mode="single" selected={due} onSelect={(d) => { setDue(d); setDueOpen(false); }} locale={es} initialFocus />
                {due && (
                  <button type="button" onClick={() => { setDue(null); setDueOpen(false); }} data-testid="clear-due"
                    className="w-full text-xs text-zinc-400 hover:text-white py-2.5 border-t border-white/10 transition-colors">
                    Quitar fecha
                  </button>
                )}
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <button type="submit" disabled={saving || !boards.length} data-testid="create-task-submit"
          className="w-full bg-primary hover:bg-[#FF5047] text-white font-semibold rounded-lg py-2.5 text-sm disabled:opacity-60">
          {saving ? "Creando…" : "Crear tarea"}
        </button>
      </form>
    </div>
  );
}

export default function MyTasksPage() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState(null);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [boards, setBoards] = useState([]);
  const [active, setActive] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const [warnMsg, setWarnMsg] = useState(null);
  const [pendingChange, setPendingChange] = useState(null);

  const load = useCallback(async () => {
    try {
      const [t, u, d, b] = await Promise.all([
        api.get("/my-tasks"), api.get("/users"), api.get("/departments"), api.get("/boards"),
      ]);
      setTasks(t.data); setUsers(u.data); setDepartments(d.data); setBoards(b.data);
    } catch {
      toast.error("No se pudieron cargar tus tareas");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const depName = useMemo(() => Object.fromEntries(departments.map((d) => [d.id, d.name])), [departments]);

  if (!tasks) return <PageSkeleton />;

  const searchMatch = (t) => !q || t.title.toLowerCase().includes(q.toLowerCase());
  const activeRows = tasks.filter((t) => {
    if (!searchMatch(t) || t.is_done || filter === "done" || filter === "archived") return false;
    if (filter === "overdue") return dueState(t.due_date) === "overdue";
    return true;
  });
  const doneRows = tasks
    .filter((t) => t.is_done && !t.is_archived && searchMatch(t))
    .sort((a, b) => (b.status_changed_at || "").localeCompare(a.status_changed_at || ""));
  const archivedRows = tasks
    .filter((t) => t.is_archived && searchMatch(t))
    .sort((a, b) => (b.status_changed_at || "").localeCompare(a.status_changed_at || ""));
  const doneExpanded = showDone || filter === "done";
  const pending = tasks.filter((t) => !t.is_done).length;
  const overdueN = tasks.filter((t) => !t.is_done && dueState(t.due_date) === "overdue").length;

  const doChangeStatus = async (t, status) => {
    const newCol = (t.board?.columns || []).find((c) => c.id === status);
    if (newCol?.title.toLowerCase().startsWith("complet") && !t.is_done) celebrate();
    setTasks((prev) => prev.map((x) => (x.id === t.id ? { ...x, status } : x)));
    try {
      await api.put(`/tasks/${t.id}`, { status });
      load();
    } catch {
      toast.error("No se pudo cambiar el estado");
      load();
    }
  };

  const changeStatus = (t, status) => {
    const rule = statusRule(t.board?.columns || [], t.status, status);
    if (rule === "block-back") { setWarnMsg("La tarea ya fue iniciada y no puede regresar al estado «Planeado»."); return; }
    if (rule === "confirm-start") { setPendingChange({ t, status }); return; }
    doChangeStatus(t, status);
  };

  return (
    <div className="h-screen overflow-y-auto">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div>
          <h1 className="font-display text-3xl font-extrabold" data-testid="my-tasks-title">Mis tareas</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Gestiona y organiza tu trabajo · {pending} pendiente{pending !== 1 ? "s" : ""}
            {overdueN > 0 && <span className="text-red-400"> · {overdueN} vencida{overdueN !== 1 ? "s" : ""}</span>}
          </p>
        </div>

        <div className="bg-[#121214] border border-white/10 rounded-2xl overflow-hidden">
          <div className="flex flex-wrap items-center gap-3 p-4 border-b border-white/10">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="mytasks-search"
                placeholder="Buscar tareas…" className={`${inputCls} w-full pl-9`} />
            </div>
            <div className="inline-flex items-center gap-2 bg-[#0A0A0B] border border-white/10 rounded-lg pl-2.5 pr-1 py-[7px]">
              <Filter className="h-3.5 w-3.5 text-zinc-600" />
              <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="mytasks-filter"
                className="bg-transparent text-sm outline-none cursor-pointer">
                <option value="all" className="bg-[#121214]">Todas las tareas</option>
                <option value="pending" className="bg-[#121214]">Pendientes</option>
                <option value="overdue" className="bg-[#121214]">Vencidas</option>
                <option value="done" className="bg-[#121214]">Completadas</option>
                <option value="archived" className="bg-[#121214]">Archivadas</option>
              </select>
            </div>
            <div className="flex-1" />
            <button onClick={() => setShowCreate(true)} data-testid="create-task-button"
              className="bg-primary hover:bg-[#FF5047] text-white font-semibold rounded-lg px-4 py-2 text-sm flex items-center gap-1.5">
              <Plus className="h-4 w-4" /> Crear tarea
            </button>
          </div>

          {(filter === "archived" ? archivedRows.length === 0 : activeRows.length === 0 && doneRows.length === 0) ? (
            <div className="text-center py-16 m-4 border border-dashed border-white/15 rounded-xl bg-white/[0.02]">
              <Inbox className="h-10 w-10 text-[#00B0FF] mx-auto mb-3 animate-float" />
              <p className="text-zinc-500">{filter === "archived" ? "No hay tareas archivadas (finalizadas hace más de 30 días)." : tasks.length === 0 ? "No tienes tareas asignadas todavía." : "Sin resultados para este filtro."}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="mytasks-table">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-zinc-500 border-b border-white/10">
                    <th className="px-4 py-3 font-semibold">Tarea</th>
                    <th className="px-4 py-3 font-semibold">Estado</th>
                    <th className="px-4 py-3 font-semibold">Prioridad</th>
                    <th className="px-4 py-3 font-semibold">Departamento</th>
                    <th className="px-4 py-3 font-semibold">Avance</th>
                    <th className="px-4 py-3 font-semibold"><span className="flex items-center gap-1"><Paperclip className="h-3 w-3" /> Adjuntos</span></th>
                    <th className="px-4 py-3 font-semibold">Responsables</th>
                    <th className="px-4 py-3 font-semibold"><span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> Vencimiento</span></th>
                  </tr>
                </thead>
                {GROUPS.map((g) => {
                  const items = activeRows.filter((t) => bucketOf(t) === g.id)
                    .sort((a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999"));
                  if (items.length === 0) return null;
                  return (
                    <tbody key={g.id} data-testid={`group-${g.id}`}>
                      <tr>
                        <td colSpan={8} className="px-4 pt-5 pb-2">
                          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider" style={{ color: g.color }}>
                            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: g.color }} />
                            {g.label} · {items.length}
                          </span>
                        </td>
                      </tr>
                      {items.map((t, ri) => (
                        <Row key={t.id} t={t} users={users} depName={depName} onOpen={setActive} onStatus={changeStatus} groupColor={g.color} delay={Math.min(ri * 45, 450)} />
                      ))}
                    </tbody>
                  );
                })}
                {activeRows.length === 0 && filter !== "done" && filter !== "archived" && (
                  <tbody>
                    <tr><td colSpan={8} className="px-4 py-6 text-center text-sm text-zinc-500" data-testid="no-active-tasks">No tienes tareas activas. ¡Buen trabajo!</td></tr>
                  </tbody>
                )}
                {doneRows.length > 0 && filter !== "archived" && (
                  <tbody data-testid="group-done">
                    <tr>
                      <td colSpan={8} className="px-4 pt-5 pb-2">
                        <button onClick={() => setShowDone((s) => !s)} data-testid="toggle-done-section"
                          className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#00E676] hover:opacity-80 transition-opacity">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          Finalizadas · {doneRows.length}
                          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${doneExpanded ? "rotate-180" : ""}`} />
                        </button>
                      </td>
                    </tr>
                    {doneExpanded && doneRows.map((t, ri) => (
                      <Row key={t.id} t={t} users={users} depName={depName} onOpen={setActive} onStatus={changeStatus} groupColor="#00E676" delay={Math.min(ri * 45, 450)} />
                    ))}
                  </tbody>
                )}
                {filter === "archived" && archivedRows.length > 0 && (
                  <tbody data-testid="group-archived">
                    <tr>
                      <td colSpan={8} className="px-4 pt-5 pb-2">
                        <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-500">
                          <Archive className="h-3.5 w-3.5" />
                          Archivadas (finalizadas hace +30 días) · {archivedRows.length}
                        </span>
                      </td>
                    </tr>
                    {archivedRows.map((t, ri) => (
                      <Row key={t.id} t={t} users={users} depName={depName} onOpen={setActive} onStatus={changeStatus} groupColor="#71717A" delay={Math.min(ri * 45, 450)} />
                    ))}
                  </tbody>
                )}
              </table>
            </div>
          )}
        </div>
      </div>

      {active && (
        <TaskModal task={active} board={active.board} users={users} onClose={() => setActive(null)}
          onUpdated={() => load()}
          onDeleted={() => { setActive(null); load(); }} />
      )}

      {showCreate && (
        <CreateTaskDialog boards={boards} departments={departments} meId={user?.id}
          onClose={() => setShowCreate(false)} onCreated={load} />
      )}

      {warnMsg && (
        <ConfirmDialog title="Acción no permitida" message={warnMsg} confirmLabel="Entendido" cancelLabel={null} tone="warn"
          onConfirm={() => setWarnMsg(null)} onCancel={() => setWarnMsg(null)} />
      )}
      {pendingChange && (
        <ConfirmDialog title="¿Iniciar tarea?"
          message="La tarea tomará la fecha de hoy como inicio, desaparecerá del estado «Planeado» y ya no podrá regresar al estado inicial. ¿Estás seguro?"
          confirmLabel="Sí, iniciar" tone="warn"
          onConfirm={() => { doChangeStatus(pendingChange.t, pendingChange.status); setPendingChange(null); }}
          onCancel={() => setPendingChange(null)} />
      )}
    </div>
  );
}
